import { useSeoMeta } from '@unhead/react';
import { Star } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { ArticleCard } from '@/components/ArticleCard';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useTopStarredArticles } from '@/hooks/useTopStarredArticles';

/**
 * "Most starred articles" leaderboard (`/stars`) — ranks recent wiki
 * articles by their NIP-25 star count (see `useTopStarredArticles`).
 * Linked from the homepage hero and the "Most starred" button next to
 * "Recent articles".
 */
export function TopStarredPage() {
  const { data: ranked, isLoading } = useTopStarredArticles(24);

  useSeoMeta({
    title: 'Most starred articles — Wikistar',
    description: 'The wiki articles the Wikistar community has starred the most.',
  });

  return (
    <Layout>
      <div className="mx-auto max-w-5xl px-4 py-8">
        <div className="mb-2 flex items-center gap-2">
          <Star className="size-6 fill-current text-primary" />
          <h1 className="text-2xl font-bold tracking-tight">Most starred articles</h1>
        </div>
        <p className="mb-6 text-sm text-muted-foreground">
          Ranked by stars — Wikistar's community upvote, backed by NIP-25 reactions. Star an
          article to help others find the best writing.
        </p>

        {isLoading ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <Card key={i}>
                <CardContent className="space-y-3 pt-6">
                  <Skeleton className="h-5 w-3/4" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-5/6" />
                  <div className="flex items-center gap-2 pt-2">
                    <Skeleton className="size-5 rounded-full" />
                    <Skeleton className="h-3 w-20" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : ranked && ranked.length > 0 ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {ranked.map(({ article }, i) => (
              <div key={article.id} className="relative min-w-0">
                {i < 3 && (
                  <div className="absolute -top-2 -left-2 z-10 flex size-7 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground shadow-sm">
                    {i + 1}
                  </div>
                )}
                <ArticleCard event={article} />
              </div>
            ))}
          </div>
        ) : (
          <Card className="border-dashed">
            <CardContent className="py-12 px-8 text-center">
              <p className="mx-auto max-w-sm text-muted-foreground">
                No starred articles yet. Be the first to star one you love!
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
